import { Component, HostListener, OnInit } from '@angular/core';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { finalize } from 'rxjs';
import { NotificationService } from '../shared/notification.service';
import {
  AuthFieldLookup,
  BrandLookup,
  emptyFleet,
  FleetAuthRequirement,
  FleetAuthFieldUpdate,
  FleetConfiguration,
  FleetConfigurationIssue,
  FleetLookups,
  FleetMatrixRule,
  FleetOverview,
  FleetSeller,
  FleetSkuException,
  FleetSummary,
  LookupItem,
  SellerLookup,
  TyreLookup
} from './fleet-maintenance.models';
import { FleetMaintenanceService } from './fleet-maintenance.service';

type FleetSection = FleetConfigurationIssue['section'];
type AvailabilityMode = 'franchises' | 'branches';
type FleetAuthMode = 'eauth' | 'manual' | 'email';
type FleetAuthSubType = 1 | 2;

interface FleetEditorError {
  message: string;
  section: FleetSection;
  rowId: number | null;
}

@Component({
  selector: 'app-fleet-maintenance',
  templateUrl: './fleet-maintenance.component.html',
  styleUrls: ['./fleet-maintenance.component.less']
})
export class FleetMaintenanceComponent implements OnInit {
  fleets: FleetSummary[] = [];
  configuration: FleetConfiguration | null = null;
  lookups: FleetLookups = {
    priceMethods: [],
    franchises: [],
    customStockGroups: [],
    authFields: [],
    rimSizes: []
  };

  isLoading = false;
  isSaving = false;
  isUpdatingVisibility = false;
  isDirty = false;
  loadError: string | null = null;
  saveError: FleetEditorError | null = null;
  activeTabIndex = 0;
  nameInvalid = false;
  private loadedOverview: FleetOverview | null = null;
  private loadedDocumentBase64: string | null = null;
  private loadedAuthFieldsState: string | null = null;
  private loadedMatrixRuleStates = new Map<number, string>();
  listQuery = '';
  matrixQuery = '';
  skuQuery = '';
  sellerQuery = '';

  brandOptions: BrandLookup[] = [];
  tyreOptions: TyreLookup[] = [];
  sellerOptions: SellerLookup[] = [];
  merchantNumberOptions: string[] = [];
  isSearchingBrands = false;
  isSearchingTyres = false;
  isSearchingSellers = false;
  isSearchingMerchantNumbers = false;
  private brandSearchSequence = 0;
  private tyreSearchSequence = 0;
  private sellerSearchSequence = 0;
  private merchantSearchSequence = 0;

  merchantMode: 'shared' | 'perBranch' = 'shared';
  sharedMerchantNumber: string | null = null;
  availabilityMode: AvailabilityMode | null = null;

  newBrandIds: number[] = [];
  newMatrixRim: number | null = null;
  newMatrixCommercial = false;
  newMatrixMethodId = 1;
  newMatrixValue = 0;
  newSkuIds: number[] = [];
  newSkuMode: 'fixed' | 'method' = 'method';
  newSkuFixedPrice = 0;
  newSkuMethodId = 1;
  newSkuMethodValue = 0;
  skuTablePageIndex = 1;
  newSellerIds: number[] = [];
  sellerTablePageIndex = 1;
  authFieldToAdd: number | null = null;
  authTablePageIndex = 1;

  createModalVisible = false;
  createMode: 'blank' | 'clone' = 'blank';
  cloneFleetId: number | null = null;
  cloneSections = {
    pricing: true,
    skuExceptions: true,
    availability: true,
    authFields: true,
    document: true
  };

  newFieldModalVisible = false;
  newField: FleetAuthRequirement = this.blankNewField();
  editFieldModalVisible = false;
  isUpdatingAuthField = false;
  editingAuthField: AuthFieldLookup | null = null;
  authFieldUpdate: FleetAuthFieldUpdate = { fleetAuthFieldName: '', tooltipText: null };
  documentLabel = '';

  constructor(
    private fleetService: FleetMaintenanceService,
    private notification: NotificationService
  ) { }

  ngOnInit(): void {
    this.loadLookups();
    this.loadFleets();
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent): void {
    if (this.isDirty) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  get isEditing(): boolean {
    return this.configuration !== null;
  }

  get isProtectedAuthType(): boolean {
    return this.configuration?.overview.fleetAuthType === 9;
  }

  get selectedAuthMode(): FleetAuthMode | null {
    const overview = this.configuration?.overview;
    if (!overview) return null;
    if (overview.fleetAuthType === 1) return 'eauth';
    if (overview.fleetAuthType === 2 && overview.fleetAuthSubType === 1) return 'manual';
    if (overview.fleetAuthType === 2 && overview.fleetAuthSubType === 2) return 'email';
    return null;
  }

  get authConfigurationError(): string | null {
    const overview = this.configuration?.overview;
    if (!overview || overview.fleetAuthType === 9) return null;
    if (overview.fleetAuthType === 1) {
      return overview.fleetAuthSubType === 1 || overview.fleetAuthSubType === 2
        ? null
        : 'Choose Manual Auth or Email Auth as the eAuth fallback.';
    }
    if (overview.fleetAuthType === 2) {
      return overview.fleetAuthSubType === 1 || overview.fleetAuthSubType === 2
        ? null
        : 'Choose Manual Auth or Email Auth.';
    }
    return 'Choose eAuth, Manual Auth or Email Auth.';
  }

  get authConfigurationWasChanged(): boolean {
    return this.authConfigurationChanged();
  }

  get activeCount(): number {
    return this.fleets.filter(fleet => fleet.isVisible).length;
  }

  get hiddenCount(): number {
    return this.fleets.filter(fleet => !fleet.isVisible).length;
  }

  get completeSetupCount(): number {
    return this.fleets.filter(fleet => fleet.isComplete).length;
  }

  get callCentreNumberError(): string | null {
    const value = this.configuration?.overview.callCentreNumber?.trim();
    if (!value) return null;
    const normalised = value.replace(/[\s()\-]/g, '');
    return /^(?:0\d{9}|\+27\d{9})$/.test(normalised)
      ? null
      : 'Enter a 10-digit South African number starting with 0, or +27 followed by 9 digits.';
  }

  get callCentreEmailError(): string | null {
    const value = this.configuration?.overview.callCentreEmail?.trim();
    if (!value) return null;
    return /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(value)
      ? null
      : 'Enter a complete email address, for example fleet@example.co.za.';
  }

  get fleetSuggestions(): FleetSummary[] {
    const query = this.listQuery.trim().toLowerCase();
    const matches = query
      ? this.fleets.filter(fleet => fleet.name.toLowerCase().includes(query))
      : this.fleets;
    return matches.slice(0, 8);
  }

  setupStatusTooltip(fleet: FleetSummary): string {
    return fleet.missingRequirements.length
      ? `Missing: ${fleet.missingRequirements.join('; ')}.`
      : 'All required fleet information is available.';
  }

  get matrixFilterSuggestions(): string[] {
    return this.suggestionsFor(
      (this.configuration?.matrixRules || []).reduce<Array<string | null | undefined>>(
        (values, rule) => values.concat([
          rule.brandName,
          rule.rim === null ? 'Any rim' : `${rule.rim} rim`,
          rule.isCommercial ? 'Commercial' : 'Non-commercial',
          rule.method
        ]),
        []
      ),
      this.matrixQuery
    );
  }

  get skuFilterSuggestions(): string[] {
    return this.suggestionsFor(
      (this.configuration?.skuExceptions || []).reduce<Array<string | null | undefined>>(
        (values, item) => values.concat([item.stockCode, item.description]),
        []
      ),
      this.skuQuery
    );
  }

  get sellerFilterSuggestions(): string[] {
    return this.suggestionsFor(
      (this.configuration?.sellers || []).reduce<Array<string | null | undefined>>(
        (values, seller) => values.concat([
          seller.sellerName,
          seller.merchantNumber,
          this.franchiseName(seller.franchiseId)
        ]),
        []
      ),
      this.sellerQuery
    );
  }

  get selectedMatrixRuleCount(): number {
    return this.configuration?.matrixRules.filter(rule => rule.selected).length || 0;
  }

  get selectedSkuExceptionCount(): number {
    return this.configuration?.skuExceptions.filter(item => item.selected).length || 0;
  }

  get selectedSellerCount(): number {
    return this.configuration?.sellers.filter(seller => seller.selected).length || 0;
  }

  get availabilityCount(): number {
    if (!this.configuration) return 0;
    if (this.availabilityMode === 'franchises') return this.configuration.franchiseIds.length;
    if (this.availabilityMode === 'branches') return this.configuration.sellers.length;
    return this.configuration.franchiseIds.length + this.configuration.sellers.length;
  }

  get hasConflictingAvailability(): boolean {
    return !!this.configuration?.franchiseIds.length && !!this.configuration?.sellers.length;
  }

  get availabilityValidationMessage(): string | null {
    if (!this.configuration) return 'Fleet availability is unavailable.';
    if (!this.availabilityMode) {
      return this.hasConflictingAvailability
        ? 'Choose either entire franchises or specific branches. This fleet currently has both.'
        : 'Choose how this fleet will be assigned.';
    }
    if (this.availabilityMode === 'franchises' && this.configuration.franchiseIds.length === 0) {
      return 'Select at least one Tyremart or Tyreworx franchise.';
    }
    if (this.availabilityMode === 'branches' && this.configuration.sellers.length === 0) {
      return 'Add at least one active Tyremart or Tyreworx branch.';
    }
    return null;
  }

  get newMatrixRimError(): string | null {
    return this.rimValidationMessage(this.newMatrixRim);
  }

  get newMatrixValueError(): string | null {
    return this.pricingMethodValidationMessage(this.newMatrixMethodId, this.newMatrixValue);
  }

  get newSkuExceptionValueError(): string | null {
    return this.newSkuMode === 'fixed'
      ? this.fixedStockCodePriceValidationMessage(this.newSkuFixedPrice)
      : this.pricingMethodValidationMessage(this.newSkuMethodId, this.newSkuMethodValue);
  }

  get matrixPriceMethods(): LookupItem[] {
    return this.lookups.priceMethods.filter(method => method.id !== 3);
  }

  get hasSelectedMatrixRules(): boolean {
    return !!this.configuration?.matrixRules.some(rule => rule.selected);
  }

  get hasSelectedSkuExceptions(): boolean {
    return !!this.configuration?.skuExceptions.some(item => item.selected);
  }

  get hasSelectedSellers(): boolean {
    return !!this.configuration?.sellers.some(seller => seller.selected);
  }

  get pricingRuleIssues(): string[] {
    const issues = (this.configuration?.issues || [])
      .filter(issue => issue.section === 'pricingRules')
      .map(issue => issue.message);
    const validationIssues = this.configuration
      ? this.configuration.matrixRules
        .map(rule => this.matrixRuleValidationMessage(rule))
        .filter((message): message is string => !!message)
      : [];
    if (this.saveError?.section === 'pricingRules') issues.push(this.saveError.message);
    return [...new Set([...issues, ...validationIssues])];
  }

  get stockCodeExceptionIssues(): string[] {
    const issues = (this.configuration?.issues || [])
      .filter(issue => issue.section === 'stockCodeExceptions')
      .map(issue => issue.message);
    const validationIssues = this.configuration
      ? this.configuration.skuExceptions
        .map(item => this.skuExceptionValidationMessage(item))
        .filter((message): message is string => !!message)
      : [];
    if (this.saveError?.section === 'stockCodeExceptions') issues.push(this.saveError.message);
    return [...new Set([...issues, ...validationIssues])];
  }

  get availabilityIssues(): string[] {
    const issues = (this.configuration?.issues || [])
      .filter(issue => issue.section === 'availability')
      .map(issue => issue.message);
    if (this.saveError?.section === 'availability') issues.push(this.saveError.message);
    return [...new Set(issues)];
  }

  matrixRuleHasIssue(rule: FleetMatrixRule): boolean {
    return !!this.matrixRuleIssueMessage(rule);
  }

  skuExceptionHasIssue(item: FleetSkuException): boolean {
    return !!this.skuExceptionIssueMessage(item);
  }

  get authFieldsAvailable(): AuthFieldLookup[] {
    if (!this.configuration) return [];
    const selected = new Set(
      this.configuration.authFields
        .map(field => field.fleetAuthFieldId)
        .filter((id): id is number => id !== null)
    );
    return this.lookups.authFields.filter(field => !selected.has(field.fleetAuthFieldId));
  }

  get authFieldUpdateNameError(): string | null {
    const name = this.authFieldUpdate.fleetAuthFieldName.trim();
    if (!name) return 'Enter a field name.';
    const duplicate = this.lookups.authFields.some(field =>
      field.fleetAuthFieldId !== this.editingAuthField?.fleetAuthFieldId &&
      field.fleetAuthFieldName.trim().toLowerCase() === name.toLowerCase()
    );
    return duplicate ? 'Another authorisation field already uses this name.' : null;
  }

  get filteredMatrixRules(): FleetMatrixRule[] {
    if (!this.configuration) return [];
    const query = this.matrixQuery.trim().toLowerCase();
    if (!query) return this.configuration.matrixRules;
    return this.configuration.matrixRules.filter(rule =>
      (rule.brandName || '').toLowerCase().includes(query) ||
      String(rule.rim ?? 'any rim').toLowerCase().includes(query) ||
      (rule.method || '').toLowerCase().includes(query) ||
      (rule.isCommercial ? 'commercial' : 'non-commercial').includes(query)
    );
  }

  get filteredSkuExceptions(): FleetSkuException[] {
    if (!this.configuration) return [];
    const query = this.skuQuery.trim().toLowerCase();
    if (!query) return this.configuration.skuExceptions;
    return this.configuration.skuExceptions.filter(item =>
      (item.stockCode || '').toLowerCase().includes(query) ||
      (item.description || '').toLowerCase().includes(query)
    );
  }

  get filteredSellers(): FleetSeller[] {
    if (!this.configuration) return [];
    const query = this.sellerQuery.trim().toLowerCase();
    if (!query) return this.configuration.sellers;
    return this.configuration.sellers.filter(seller =>
      (seller.sellerName || '').toLowerCase().includes(query) ||
      (seller.merchantNumber || '').toLowerCase().includes(query) ||
      this.franchiseName(seller.franchiseId).toLowerCase().includes(query)
    );
  }

  loadFleets(): void {
    this.isLoading = true;
    this.fleetService.getFleets(this.listQuery, true)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: response => {
          this.fleets = response.items;
          this.loadError = null;
        },
        error: error => this.setLoadError(error, 'Could not load fleets.')
      });
  }

  loadLookups(): void {
    this.fleetService.getLookups().subscribe({
      next: response => {
        this.lookups = response;
        this.loadError = null;
      },
      error: error => this.setLoadError(error, 'Could not load fleet-maintenance options.')
    });
  }

  retryInitialLoad(): void {
    this.loadError = null;
    this.loadLookups();
    this.loadFleets();
  }

  editFleet(fleetId: number): void {
    if (!this.canLeaveEditor()) return;
    this.isLoading = true;
    this.fleetService.getFleet(fleetId)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: configuration => this.useConfiguration(configuration),
        error: error => this.notification.handleError(error, 'Could not load this fleet.')
      });
  }

  openCreateModal(): void {
    if (!this.canLeaveEditor()) return;
    this.createMode = 'blank';
    this.cloneFleetId = null;
    this.createModalVisible = true;
  }

  startCreate(): void {
    if (this.createMode === 'blank') {
      this.useConfiguration(emptyFleet(), true);
      this.createModalVisible = false;
      return;
    }
    if (!this.cloneFleetId) {
      this.notification.showWarning('Select a fleet to copy.');
      return;
    }
    this.isLoading = true;
    this.fleetService.getFleet(this.cloneFleetId)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: source => {
          const clone = emptyFleet();
          clone.overview.name = `COPY OF ${source.overview.name}`.toUpperCase().substring(0, 50);
          clone.overview.callCentreNumber = source.overview.callCentreNumber;
          clone.overview.callCentreEmail = source.overview.callCentreEmail;
          clone.overview.customStockGroupId = source.overview.customStockGroupId;
          clone.overview.hasDynamicPricing = source.overview.hasDynamicPricing;
          if (source.overview.fleetAuthType === 1 || source.overview.fleetAuthType === 2) {
            clone.overview.fleetAuthType = source.overview.fleetAuthType;
            clone.overview.fleetAuthSubType =
              source.overview.fleetAuthSubType === 1 || source.overview.fleetAuthSubType === 2
                ? source.overview.fleetAuthSubType
                : null;
          }
          if (this.cloneSections.pricing) {
            clone.matrixRules = source.matrixRules
              .filter(rule => rule.priceMatrixMethodId !== 3)
              .map(rule => ({ ...this.copy(rule), specialPriceMatrixId: null, selected: false }));
          }
          if (this.cloneSections.skuExceptions) {
            clone.skuExceptions = source.skuExceptions.map(item => ({ ...this.copy(item), specialPriceItemId: null, selected: false }));
          }
          if (this.cloneSections.availability) {
            clone.franchiseIds = [...source.franchiseIds];
            clone.sellers = source.sellers.map(seller => ({ ...this.copy(seller), specialPriceSetSellerId: null, selected: false }));
          }
          if (this.cloneSections.authFields) {
            clone.authFields = source.authFields.map(field => ({
              ...this.copy(field), fleetAuthFieldRequirementId: null, selected: false
            }));
          }
          if (this.cloneSections.document) clone.documentBase64 = source.documentBase64;
          this.useConfiguration(clone, true);
          this.createModalVisible = false;
        },
        error: error => this.notification.handleError(error, 'Could not copy the selected fleet.')
      });
  }

  saveFleet(): void {
    this.saveError = null;
    this.nameInvalid = !this.configuration?.overview.name.trim();
    if (!this.configuration || this.nameInvalid) {
      this.activeTabIndex = 0;
      this.notification.showWarning('Enter a fleet name before saving.');
      return;
    }
    const overviewError = this.overviewValidationMessageForSave();
    if (overviewError) {
      this.setSaveError(overviewError, 'overview');
      return;
    }
    if (this.authConfigurationError && this.authConfigurationChanged()) {
      this.setSaveError(this.authConfigurationError, 'overview');
      return;
    }
    const invalidMatrixRule = this.configuration.matrixRules.find(rule => !!this.matrixRuleValidationMessage(rule));
    if (invalidMatrixRule) {
      this.setSaveError(
        this.matrixRuleValidationMessage(invalidMatrixRule) as string,
        'pricingRules',
        invalidMatrixRule.specialPriceMatrixId
      );
      return;
    }
    const invalidStockException = this.configuration.skuExceptions.find(item => !!this.skuExceptionValidationMessage(item));
    if (invalidStockException) {
      this.setSaveError(
        this.skuExceptionValidationMessage(invalidStockException) as string,
        'stockCodeExceptions',
        invalidStockException.specialPriceItemId
      );
      return;
    }
    const availabilityError = this.availabilityValidationMessage;
    if (availabilityError && (this.availabilityMode !== null || !this.configuration.specialPriceSetId)) {
      this.setSaveError(availabilityError, 'availability');
      return;
    }
    this.isSaving = true;
    const configuration = this.configurationForSave();
    const request = configuration.specialPriceSetId
      ? this.fleetService.updateFleet(configuration)
      : this.fleetService.createFleet(configuration);
    request.pipe(finalize(() => this.isSaving = false)).subscribe({
      next: response => {
        this.notification.showSuccess(response.message);
        this.isDirty = false;
        this.saveError = null;
        this.loadFleets();
        this.reloadFleet(response.specialPriceSetId);
      },
      error: error => {
        if (error.status === 409) {
          const message = this.errorMessage(error, 'This fleet changed. Reload it before saving.');
          if (window.confirm(`${message}\n\nReload the latest version now?`)) {
            this.reloadFleet(configuration.specialPriceSetId as number);
          } else {
            this.setSaveError(message, 'overview');
          }
        } else {
          this.captureSaveError(error, 'Could not save this fleet.');
        }
      }
    });
  }

  toggleVisibility(): void {
    if (!this.configuration?.specialPriceSetId || !this.configuration.versionHash) return;
    const fleetId = this.configuration.specialPriceSetId;
    const isVisible = !this.configuration.overview.isVisible;
    this.isUpdatingVisibility = true;
    this.fleetService.updateVisibility(
      fleetId,
      isVisible,
      this.configuration.versionHash
    ).pipe(finalize(() => this.isUpdatingVisibility = false)).subscribe({
      next: response => {
        if (this.configuration?.specialPriceSetId === fleetId) {
          this.configuration.overview.isVisible = isVisible;
          this.configuration.versionHash = response.versionHash;
        }
        if (this.loadedOverview) this.loadedOverview.isVisible = isVisible;
        this.fleets = this.fleets.map(fleet =>
          fleet.specialPriceSetId === fleetId ? { ...fleet, isVisible } : fleet
        );
        this.notification.showSuccess(response.message);
      },
      error: error => {
        if (error.status === 409) {
          const message = this.errorMessage(error, 'This fleet changed. Reload it before changing visibility.');
          if (window.confirm(`${message}\n\nReload the latest version now? Unsaved changes will be lost.`)) {
            this.reloadFleet(fleetId);
          } else {
            this.setSaveError(message, 'overview');
          }
        } else {
          this.notification.handleError(error, 'Could not change fleet visibility.');
        }
      }
    });
  }

  backToList(): void {
    if (!this.canLeaveEditor()) return;
    this.configuration = null;
    this.loadedOverview = null;
    this.loadedDocumentBase64 = null;
    this.isDirty = false;
    this.saveError = null;
    this.documentLabel = '';
  }

  canDeactivate(): boolean {
    return this.canLeaveEditor();
  }

  markDirty(): void {
    this.isDirty = true;
    this.saveError = null;
  }

  searchBrands(query: string): void {
    const requestId = ++this.brandSearchSequence;
    this.isSearchingBrands = true;
    this.fleetService.searchBrands(query)
      .pipe(finalize(() => {
        if (requestId === this.brandSearchSequence) this.isSearchingBrands = false;
      }))
      .subscribe({
        next: response => {
          if (requestId !== this.brandSearchSequence) return;
          this.brandOptions = this.searchOptionsWithSelected(
            this.brandOptions, response.items, 'brandId', this.newBrandIds
          );
        },
        error: error => {
          if (requestId === this.brandSearchSequence) this.handleLookupError(error, 'Could not search brands.');
        }
      });
  }

  searchTyres(query: string): void {
    const requestId = ++this.tyreSearchSequence;
    this.isSearchingTyres = true;
    this.fleetService.searchTyres(query)
      .pipe(finalize(() => {
        if (requestId === this.tyreSearchSequence) this.isSearchingTyres = false;
      }))
      .subscribe({
        next: response => {
          if (requestId !== this.tyreSearchSequence) return;
          this.tyreOptions = this.searchOptionsWithSelected(
            this.tyreOptions, response.items, 'msfid', this.newSkuIds
          );
        },
        error: error => {
          if (requestId === this.tyreSearchSequence) this.handleLookupError(error, 'Could not search tyres.');
        }
      });
  }

  searchSellers(query: string): void {
    const requestId = ++this.sellerSearchSequence;
    this.isSearchingSellers = true;
    this.fleetService.searchSellers(query)
      .pipe(finalize(() => {
        if (requestId === this.sellerSearchSequence) this.isSearchingSellers = false;
      }))
      .subscribe({
        next: response => {
          if (requestId !== this.sellerSearchSequence) return;
          this.sellerOptions = this.searchOptionsWithSelected(
            this.sellerOptions, response.items, 'sellerId', this.newSellerIds
          );
        },
        error: error => {
          if (requestId === this.sellerSearchSequence) this.handleLookupError(error, 'Could not search branches.');
        }
      });
  }

  searchMerchantNumbers(query: string): void {
    const requestId = ++this.merchantSearchSequence;
    this.isSearchingMerchantNumbers = true;
    this.fleetService.searchMerchantNumbers(query)
      .pipe(finalize(() => {
        if (requestId === this.merchantSearchSequence) this.isSearchingMerchantNumbers = false;
      }))
      .subscribe({
        next: response => {
          if (requestId !== this.merchantSearchSequence) return;
          const values = response.items.map(item => item.merchantNumber);
          const selected = this.distinctMerchantNumbers(this.configuration?.sellers || []);
          if (this.sharedMerchantNumber?.trim()) selected.unshift(this.sharedMerchantNumber.trim());
          this.merchantNumberOptions = [...new Set([...selected, ...values])];
        },
        error: error => {
          if (requestId === this.merchantSearchSequence) {
            this.handleLookupError(error, 'Could not search merchant numbers.');
          }
        }
      });
  }

  setAuthMode(mode: FleetAuthMode): void {
    if (!this.configuration || this.isProtectedAuthType) return;
    const overview = this.configuration.overview;
    if (mode === 'eauth') {
      const keepCurrentFallback = overview.fleetAuthType === 1 &&
        (overview.fleetAuthSubType === 1 || overview.fleetAuthSubType === 2);
      overview.fleetAuthType = 1;
      if (!keepCurrentFallback) overview.fleetAuthSubType = null;
    } else {
      overview.fleetAuthType = 2;
      overview.fleetAuthSubType = mode === 'manual' ? 1 : 2;
    }
    this.markDirty();
  }

  setEAuthFallback(fallback: FleetAuthSubType): void {
    if (!this.configuration || this.isProtectedAuthType || this.configuration.overview.fleetAuthType !== 1) return;
    this.configuration.overview.fleetAuthSubType = fallback;
    this.markDirty();
  }

  setFleetName(value: string): void {
    if (!this.configuration) return;
    this.configuration.overview.name = (value || '').toUpperCase();
    this.nameInvalid = false;
    this.markDirty();
  }

  setNewMatrixRim(value: number | string | null | undefined): void {
    this.newMatrixRim = this.normaliseRim(value);
  }

  setMatrixRuleRim(rule: FleetMatrixRule, value: number | string | null | undefined): void {
    rule.rim = this.normaliseRim(value);
    this.markDirty();
  }

  isUnavailableRim(rim: number | null): boolean {
    return rim !== null && !this.lookups.rimSizes.includes(Number(rim));
  }

  rimOptionLabel(rim: number): string {
    return `${rim}\"`;
  }

  existingRimOptionLabel(rim: number): string {
    return `Existing: ${this.rimOptionLabel(Number(rim))}`;
  }

  setMerchantMode(mode: 'shared' | 'perBranch'): void {
    if (!this.configuration || this.merchantMode === mode) return;
    this.merchantMode = mode;
    if (mode === 'shared') {
      const values = this.distinctMerchantNumbers(this.configuration.sellers);
      this.sharedMerchantNumber = values.length === 1 ? values[0] : null;
      this.searchMerchantNumbers(this.sharedMerchantNumber || '');
    }
    this.markDirty();
  }

  addMatrixRules(): void {
    if (!this.configuration || this.newBrandIds.length === 0) return;
    if (this.newMatrixMethodId === 3) {
      this.notification.showWarning(
        "Don't Sell is not needed here. Leave the brand and rim without a rule instead."
      );
      return;
    }
    const rimError = this.rimValidationMessage(this.newMatrixRim);
    if (rimError) {
      this.notification.showWarning(rimError);
      return;
    }
    const existing = new Set(this.configuration.matrixRules.map(rule => this.matrixScopeKey(rule)));
    let skippedDuplicates = 0;
    for (const brandId of this.newBrandIds) {
      const rim = this.newMatrixRim === null ? null : Number(this.newMatrixRim);
      const key = `${brandId}|${rim ?? 'default'}|${this.newMatrixCommercial}`;
      if (existing.has(key)) {
        skippedDuplicates += 1;
        continue;
      }
      const brand = this.brandOptions.find(option => option.brandId === brandId);
      this.configuration.matrixRules.push({
        specialPriceMatrixId: null,
        brandId,
        brandName: brand?.name || `Brand ${brandId}`,
        rim,
        isCommercial: this.newMatrixCommercial,
        priceMatrixMethodId: this.newMatrixMethodId,
        method: this.methodName(this.newMatrixMethodId),
        value: this.newMatrixMethodId === 3 ? 0 : this.newMatrixValue
      });
    }
    this.newBrandIds = [];
    if (skippedDuplicates) {
      this.notification.showWarning('A matching brand, rim and commercial classification already exists.');
    }
    this.markDirty();
  }

  setAvailabilityMode(mode: AvailabilityMode): void {
    if (!this.configuration || this.availabilityMode === mode) return;
    const removedCount = mode === 'franchises'
      ? this.configuration.sellers.length
      : this.configuration.franchiseIds.length;
    if (removedCount > 0) {
      const removedLabel = mode === 'franchises'
        ? `${removedCount} specific branch mapping${removedCount === 1 ? '' : 's'}`
        : `${removedCount} franchise mapping${removedCount === 1 ? '' : 's'}`;
      if (!window.confirm(`Switch availability method? Saving this fleet will remove ${removedLabel}.`)) return;
    }
    this.availabilityMode = mode;
    this.newSellerIds = [];
    this.sellerQuery = '';
    this.markDirty();
  }

  removeSelectedMatrixRules(): void {
    if (!this.configuration) return;
    const selected = this.configuration.matrixRules.filter(rule => rule.selected);
    if (!selected.length || !this.confirmRemoval('pricing rule', selected.length)) return;
    this.configuration.matrixRules = this.configuration.matrixRules.filter(rule => !rule.selected);
    this.markDirty();
  }

  setMatrixMethod(rule: FleetMatrixRule, methodId: number): void {
    rule.priceMatrixMethodId = methodId;
    rule.method = this.methodName(methodId);
    if (methodId === 3) rule.value = 0;
    this.markDirty();
  }

  setNewMatrixMethod(methodId: number): void {
    this.newMatrixMethodId = methodId;
    if (methodId === 3) this.newMatrixValue = 0;
  }

  pricingValueMax(methodId: number | null): number {
    return methodId === 1 ? 99.99 : 100;
  }

  matrixScopeKey(rule: Pick<FleetMatrixRule, 'brandId' | 'rim' | 'isCommercial'>): string {
    const rim = this.normaliseRim(rule.rim) ?? 'default';
    return `${rule.brandId}|${rim}|${rule.isCommercial}`;
  }

  matrixRuleValidationMessage(rule: FleetMatrixRule): string | null {
    const matchingScopes = this.configuration?.matrixRules.filter(candidate =>
      this.matrixScopeKey(candidate) === this.matrixScopeKey(rule)
    ).length || 0;
    if (matchingScopes > 1) {
      return 'A brand, rim and tyre classification can only have one pricing rule.';
    }
    const rimMessage = this.rimValidationMessage(rule.rim);
    if (rimMessage) return rimMessage;
    if (rule.priceMatrixMethodId === 3 && !this.isUnchangedLegacyMatrixDontSell(rule)) {
      return "Don't Sell is not needed for brand and rim pricing. Remove this rule or choose FGPP or Retail Less.";
    }
    return this.pricingMethodValidationMessage(rule.priceMatrixMethodId, rule.value);
  }

  isLegacyMatrixDontSell(rule: FleetMatrixRule): boolean {
    return rule.priceMatrixMethodId === 3 && rule.specialPriceMatrixId !== null;
  }

  matrixRuleIssueMessage(rule: FleetMatrixRule): string | null {
    const liveMessage = this.matrixRuleValidationMessage(rule);
    if (liveMessage) return liveMessage;
    const storedIssue = this.configuration?.issues.find(issue =>
      issue.section === 'pricingRules' && issue.rowId === rule.specialPriceMatrixId
    );
    if (storedIssue) return storedIssue.message;
    return this.saveError?.section === 'pricingRules' && this.saveError.rowId !== null && this.saveError.rowId === rule.specialPriceMatrixId
      ? this.saveError.message
      : null;
  }

  skuExceptionValidationMessage(item: FleetSkuException): string | null {
    const hasFixedPrice = item.price !== null;
    const hasMethodPrice = item.priceMatrixMethodId !== null && item.priceMatrixMethodValue !== null;
    if (hasFixedPrice === hasMethodPrice) {
      return 'Each stock code needs either a fixed price or a pricing method.';
    }
    if (hasFixedPrice) {
      return this.fixedStockCodePriceValidationMessage(item.price);
    }
    return this.pricingMethodValidationMessage(item.priceMatrixMethodId, item.priceMatrixMethodValue);
  }

  skuExceptionIssueMessage(item: FleetSkuException): string | null {
    const liveMessage = this.skuExceptionValidationMessage(item);
    if (liveMessage) return liveMessage;
    const storedIssue = this.configuration?.issues.find(issue =>
      issue.section === 'stockCodeExceptions' && issue.rowId === item.specialPriceItemId
    );
    if (storedIssue) return storedIssue.message;
    return this.saveError?.section === 'stockCodeExceptions' && this.saveError.rowId !== null && this.saveError.rowId === item.specialPriceItemId
      ? this.saveError.message
      : null;
  }

  readonly sortFleetName = (left: FleetSummary, right: FleetSummary) => this.compareText(left.name, right.name);
  readonly sortFleetVisibility = (left: FleetSummary, right: FleetSummary) => Number(left.isVisible) - Number(right.isVisible);
  readonly sortFleetPricing = (left: FleetSummary, right: FleetSummary) => left.matrixRuleCount - right.matrixRuleCount;
  readonly sortFleetStockExceptions = (left: FleetSummary, right: FleetSummary) => left.skuExceptionCount - right.skuExceptionCount;
  readonly sortFleetAvailability = (left: FleetSummary, right: FleetSummary) =>
    (left.sellerCount + (left.franchiseCount || 0)) - (right.sellerCount + (right.franchiseCount || 0));
  readonly sortFleetSetup = (left: FleetSummary, right: FleetSummary) => Number(left.isComplete) - Number(right.isComplete);
  readonly sortMatrixBrand = (left: FleetMatrixRule, right: FleetMatrixRule) => this.compareText(left.brandName, right.brandName);
  readonly sortMatrixRim = (left: FleetMatrixRule, right: FleetMatrixRule) => (left.rim ?? 0) - (right.rim ?? 0);
  readonly sortMatrixClassification = (left: FleetMatrixRule, right: FleetMatrixRule) => Number(left.isCommercial) - Number(right.isCommercial);
  readonly sortMatrixMethod = (left: FleetMatrixRule, right: FleetMatrixRule) => this.compareText(left.method, right.method);
  readonly sortMatrixValue = (left: FleetMatrixRule, right: FleetMatrixRule) => left.value - right.value;
  readonly sortStockCode = (left: FleetSkuException, right: FleetSkuException) => this.compareText(left.stockCode, right.stockCode);
  readonly sortStockDescription = (left: FleetSkuException, right: FleetSkuException) => this.compareText(left.description, right.description);
  readonly sortStockPriceType = (left: FleetSkuException, right: FleetSkuException) => this.compareText(this.skuMode(left), this.skuMode(right));
  readonly sortStockMethod = (left: FleetSkuException, right: FleetSkuException) => this.compareText(left.method, right.method);
  readonly sortStockValue = (left: FleetSkuException, right: FleetSkuException) => this.stockExceptionValue(left) - this.stockExceptionValue(right);
  readonly sortSellerName = (left: FleetSeller, right: FleetSeller) => this.compareText(left.sellerName, right.sellerName);
  readonly sortSellerFranchise = (left: FleetSeller, right: FleetSeller) => this.compareText(this.franchiseName(left.franchiseId), this.franchiseName(right.franchiseId));
  readonly sortSellerMerchant = (left: FleetSeller, right: FleetSeller) => this.compareText(left.merchantNumber, right.merchantNumber);
  readonly sortAuthField = (left: FleetAuthRequirement, right: FleetAuthRequirement) => this.compareText(left.fleetAuthFieldName, right.fleetAuthFieldName);
  readonly sortAuthType = (left: FleetAuthRequirement, right: FleetAuthRequirement) => left.dataType - right.dataType;
  readonly sortAuthRequired = (left: FleetAuthRequirement, right: FleetAuthRequirement) => Number(left.isRequired) - Number(right.isRequired);

  fleetAvailabilityLabel(fleet: FleetSummary): string {
    const franchiseCount = fleet.franchiseCount || 0;
    if (franchiseCount > 0 && fleet.sellerCount > 0) return 'Mixed setup';
    if (franchiseCount > 0) return `${franchiseCount} franchise${franchiseCount === 1 ? '' : 's'}`;
    return `${fleet.sellerCount} branch${fleet.sellerCount === 1 ? '' : 'es'}`;
  }

  addSkuExceptions(): void {
    if (!this.configuration) return;
    const valueError = this.newSkuExceptionValueError;
    if (valueError) {
      this.notification.showWarning(valueError);
      return;
    }
    const existing = new Set(this.configuration.skuExceptions.map(item => item.msfid));
    const additions: FleetSkuException[] = [];
    for (const msfid of this.newSkuIds) {
      if (existing.has(msfid)) continue;
      const tyre = this.tyreOptions.find(option => option.msfid === msfid);
      additions.push({
        specialPriceItemId: null,
        msfid,
        stockCode: tyre?.stockCode || String(msfid),
        description: tyre?.description || '',
        price: this.newSkuMode === 'fixed' ? this.newSkuFixedPrice : null,
        priceMatrixMethodId: this.newSkuMode === 'method' ? this.newSkuMethodId : null,
        method: this.newSkuMode === 'method' ? this.methodName(this.newSkuMethodId) : null,
        priceMatrixMethodValue: this.newSkuMode === 'method'
          ? (this.newSkuMethodId === 3 ? 0 : this.newSkuMethodValue)
          : null
      });
    }
    if (!additions.length) {
      this.notification.showWarning('The selected stock codes already have exceptions for this fleet.');
      return;
    }
    this.configuration.skuExceptions = [...additions, ...this.configuration.skuExceptions];
    this.skuTablePageIndex = 1;
    this.newSkuIds = [];
    this.markDirty();
  }

  setNewSkuMode(mode: 'fixed' | 'method'): void {
    this.newSkuMode = mode;
  }

  setNewSkuMethod(methodId: number): void {
    this.newSkuMethodId = methodId;
    if (methodId === 3) this.newSkuMethodValue = 0;
  }

  setSkuMode(item: FleetSkuException, mode: 'fixed' | 'method'): void {
    if (mode === 'fixed') {
      item.price = item.price ?? 0;
      item.priceMatrixMethodId = null;
      item.priceMatrixMethodValue = null;
    } else {
      item.price = null;
      item.priceMatrixMethodId = item.priceMatrixMethodId ?? 1;
      item.priceMatrixMethodValue = item.priceMatrixMethodValue ?? 0;
    }
    this.markDirty();
  }

  setSkuMethod(item: FleetSkuException, methodId: number): void {
    item.priceMatrixMethodId = methodId;
    item.method = this.methodName(methodId);
    if (methodId === 3) item.priceMatrixMethodValue = 0;
    this.markDirty();
  }

  skuMode(item: FleetSkuException): 'fixed' | 'method' {
    return item.price !== null ? 'fixed' : 'method';
  }

  removeSelectedSkuExceptions(): void {
    if (!this.configuration) return;
    const selected = this.configuration.skuExceptions.filter(item => item.selected);
    if (!selected.length || !this.confirmRemoval('stock code exception', selected.length)) return;
    this.configuration.skuExceptions = this.configuration.skuExceptions.filter(item => !item.selected);
    this.markDirty();
  }

  addSellers(): void {
    if (!this.configuration) return;
    const existing = new Set(this.configuration.sellers.map(item => item.sellerId));
    const additions: FleetSeller[] = [];
    for (const sellerId of this.newSellerIds) {
      if (existing.has(sellerId)) continue;
      const seller = this.sellerOptions.find(option => option.sellerId === sellerId);
      additions.push({
        specialPriceSetSellerId: null,
        sellerId,
        sellerName: seller?.name || `Branch ${sellerId}`,
        franchiseId: seller?.franchiseId || null,
        merchantNumber: null,
        isActive: true,
        isSupported: true
      });
    }
    this.newSellerIds = [];
    if (!additions.length) {
      this.notification.showWarning('The selected branches are already available for this fleet.');
      return;
    }
    this.configuration.sellers = [...additions, ...this.configuration.sellers];
    this.sellerQuery = '';
    this.sellerTablePageIndex = 1;
    this.markDirty();
  }

  removeSelectedSellers(): void {
    if (!this.configuration) return;
    const selected = this.configuration.sellers.filter(item => item.selected);
    if (!selected.length || !this.confirmRemoval('participating branch', selected.length)) return;
    this.configuration.sellers = this.configuration.sellers.filter(item => !item.selected);
    this.markDirty();
  }

  toggleFranchise(franchiseId: number, selected: boolean): void {
    if (!this.configuration) return;
    const franchises = new Set(this.configuration.franchiseIds);
    if (selected) {
      franchises.add(franchiseId);
    } else {
      franchises.delete(franchiseId);
    }
    this.configuration.franchiseIds = [...franchises].sort((left, right) => left - right);
    this.markDirty();
  }

  addExistingAuthField(): void {
    if (!this.configuration || !this.authFieldToAdd) return;
    const field = this.lookups.authFields.find(item => item.fleetAuthFieldId === this.authFieldToAdd);
    if (!field) return;
    this.configuration.authFields = [{
      ...field,
      fleetAuthFieldRequirementId: null,
      isRequired: false,
      isNew: false
    }, ...this.configuration.authFields];
    this.authFieldToAdd = null;
    this.authTablePageIndex = 1;
    this.markDirty();
  }

  openNewFieldModal(): void {
    this.newField = this.blankNewField();
    this.newFieldModalVisible = true;
  }

  addNewAuthField(): void {
    if (!this.configuration || !this.newField.fleetAuthFieldName.trim()) {
      this.notification.showWarning('Enter a field name.');
      return;
    }
    this.configuration.authFields = [this.copy(this.newField), ...this.configuration.authFields];
    this.newFieldModalVisible = false;
    this.authTablePageIndex = 1;
    this.markDirty();
  }

  removeAuthField(field: FleetAuthRequirement): void {
    if (!this.configuration) return;
    if (!this.confirmRemoval('authorisation field', 1)) return;
    this.configuration.authFields = this.configuration.authFields.filter(item => item !== field);
    this.markDirty();
  }

  openEditAuthField(field: FleetAuthRequirement): void {
    if (field.fleetAuthFieldId === null || field.isNew) return;
    this.editingAuthField = {
      fleetAuthFieldId: field.fleetAuthFieldId,
      fleetAuthFieldName: field.fleetAuthFieldName,
      tooltipText: field.tooltipText,
      dataType: field.dataType,
      displayRank: field.displayRank,
      modelField: field.modelField
    };
    this.authFieldUpdate = {
      fleetAuthFieldName: field.fleetAuthFieldName,
      tooltipText: field.tooltipText
    };
    this.editFieldModalVisible = true;
  }

  updateExistingAuthField(): void {
    if (!this.editingAuthField || this.isUpdatingAuthField) return;
    const nameError = this.authFieldUpdateNameError;
    if (nameError) {
      this.notification.showWarning(nameError);
      return;
    }
    const fleetAuthFieldName = this.authFieldUpdate.fleetAuthFieldName.trim();
    const update: FleetAuthFieldUpdate = {
      fleetAuthFieldName,
      tooltipText: this.authFieldUpdate.tooltipText?.trim() || null
    };
    this.isUpdatingAuthField = true;
    this.fleetService.updateAuthField(this.editingAuthField.fleetAuthFieldId, update).pipe(
      finalize(() => this.isUpdatingAuthField = false)
    ).subscribe({
      next: updated => {
        this.lookups.authFields = this.lookups.authFields.map(field =>
          field.fleetAuthFieldId === updated.fleetAuthFieldId ? updated : field
        );
        if (this.configuration) {
          this.configuration.authFields = this.configuration.authFields.map(field =>
            field.fleetAuthFieldId === updated.fleetAuthFieldId
              ? { ...field, fleetAuthFieldName: updated.fleetAuthFieldName, tooltipText: updated.tooltipText }
              : field
          );
        }
        this.editFieldModalVisible = false;
        this.editingAuthField = null;
        this.notification.showSuccess('Authorisation field updated everywhere it is used.');
      },
      error: error => this.notification.handleError(error, 'The authorisation field could not be updated.')
    });
  }

  cancelEditAuthField(): void {
    if (this.isUpdatingAuthField) return;
    this.editFieldModalVisible = false;
    this.editingAuthField = null;
  }

  private confirmRemoval(noun: string, count: number): boolean {
    const label = count === 1 ? noun : `${noun}s`;
    return window.confirm(`Remove ${count} ${label}? This change will be applied when you save the fleet.`);
  }

  readonly beforeDocumentUpload = (upload: NzUploadFile): boolean => {
    const file = upload as NzUploadFile & File;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      this.notification.showWarning('Procedure documents must be PDF files.');
      return false;
    }
    if ((file.size || 0) > 10 * 1024 * 1024) {
      this.notification.showWarning('Procedure documents may not exceed 10 MiB.');
      return false;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (!this.configuration || !(reader.result instanceof ArrayBuffer)) return;
      const bytes = new Uint8Array(reader.result);
      const signature = String.fromCharCode(...bytes.slice(0, 5));
      if (signature !== '%PDF-') {
        this.notification.showWarning('The selected file is not a valid PDF.');
        return;
      }
      let binary = '';
      const chunkSize = 32_768;
      for (let offset = 0; offset < bytes.length; offset += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
      }
      this.configuration.documentBase64 = btoa(binary);
      this.documentLabel = file.name;
      this.markDirty();
    };
    reader.onerror = () => this.notification.showError('Could not read the selected PDF.');
    reader.readAsArrayBuffer(file);
    return false;
  };

  previewDocument(): void {
    const value = this.configuration?.documentBase64;
    if (!value) return;
    try {
      const bytes = Uint8Array.from(atob(value), character => character.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      this.notification.showError('Could not open this procedure document.');
    }
  }

  removeDocument(): void {
    if (!this.configuration) return;
    this.configuration.documentBase64 = null;
    this.documentLabel = '';
    this.markDirty();
  }

  authTypeLabel(authType: number | null, authSubType: number | null = null): string {
    if (authType === 1 && authSubType === 1) return 'eAuth · Manual fallback';
    if (authType === 1 && authSubType === 2) return 'eAuth · Email fallback';
    if (authType === 1) return 'eAuth · Fallback not configured';
    if (authType === 2 && authSubType === 1) return 'Manual Auth';
    if (authType === 2 && authSubType === 2) return 'Email Auth';
    if (authType === 2) return 'Authentication not configured';
    if (authType === 9) return 'Eqstra (managed by StockFinder)';
    return 'Not configured (managed by StockFinder)';
  }

  authDataTypeLabel(dataType: number): string {
    return ({ 1: 'Text', 2: 'Number', 3: 'Single file', 4: 'Multiple files' } as Record<number, string>)[dataType] || `Type ${dataType}`;
  }

  methodName(methodId: number | null): string {
    return this.lookups.priceMethods.find(method => method.id === methodId)?.name || '';
  }

  franchiseName(franchiseId: number | null): string {
    return this.lookups.franchises.find(franchise => franchise.id === franchiseId)?.name || 'Unknown';
  }

  private reloadFleet(fleetId: number): void {
    this.fleetService.getFleet(fleetId).subscribe({
      next: configuration => this.useConfiguration(configuration),
      error: error => this.notification.handleError(error, 'Fleet saved, but the latest version could not be reloaded.')
    });
  }

  private useConfiguration(configuration: FleetConfiguration, dirty = false): void {
    this.configuration = configuration;
    this.loadedOverview = configuration.specialPriceSetId ? { ...configuration.overview } : null;
    this.loadedDocumentBase64 = configuration.documentBase64 ?? null;
    this.loadedAuthFieldsState = configuration.specialPriceSetId
      ? this.authFieldsEditableState(configuration.authFields)
      : null;
    this.loadedMatrixRuleStates = new Map(
      configuration.matrixRules
        .filter(rule => rule.specialPriceMatrixId !== null)
        .map(rule => [rule.specialPriceMatrixId as number, this.matrixRuleEditableState(rule)])
    );
    this.isDirty = dirty;
    this.saveError = null;
    this.activeTabIndex = 0;
    this.nameInvalid = false;
    this.documentLabel = configuration.documentBase64 ? 'Current procedure.pdf' : '';
    this.matrixQuery = '';
    this.skuQuery = '';
    this.sellerQuery = '';
    this.sellerTablePageIndex = 1;
    this.authTablePageIndex = 1;
    this.availabilityMode = this.initialAvailabilityMode(configuration);
    this.initialiseMerchantMode(configuration);
  }

  private isUnchangedLegacyMatrixDontSell(rule: FleetMatrixRule): boolean {
    if (rule.specialPriceMatrixId === null) return false;
    return this.loadedMatrixRuleStates.get(rule.specialPriceMatrixId) === this.matrixRuleEditableState(rule);
  }

  private matrixRuleEditableState(rule: FleetMatrixRule): string {
    return JSON.stringify([
      rule.brandId,
      this.normaliseRim(rule.rim),
      rule.isCommercial,
      rule.priceMatrixMethodId,
      Number(rule.value)
    ]);
  }

  private initialiseMerchantMode(configuration: FleetConfiguration): void {
    const numbers = configuration.sellers.map(seller => seller.merchantNumber?.trim() || null);
    const nonBlank = [...new Set(numbers.filter((value): value is string => value !== null))];
    const oneNumberOnEveryBranch = configuration.sellers.length > 0 &&
      nonBlank.length === 1 && numbers.every(value => value === nonBlank[0]);
    this.merchantMode = !configuration.specialPriceSetId || oneNumberOnEveryBranch ? 'shared' : 'perBranch';
    this.sharedMerchantNumber = oneNumberOnEveryBranch ? nonBlank[0] : null;
    this.merchantNumberOptions = nonBlank;
  }

  private initialAvailabilityMode(configuration: FleetConfiguration): AvailabilityMode | null {
    const hasFranchises = configuration.franchiseIds.length > 0;
    const hasBranches = configuration.sellers.length > 0;
    if (hasFranchises === hasBranches) return null;
    return hasFranchises ? 'franchises' : 'branches';
  }

  private overviewValidationMessageForSave(): string | null {
    if (!this.configuration) return 'Fleet details are unavailable.';
    const overview = this.configuration.overview;
    if (this.overviewFieldChanged('name') && overview.name !== overview.name.toUpperCase()) {
      return 'Fleet names must use capital letters.';
    }
    if (this.overviewFieldChanged('callCentreNumber') && this.callCentreNumberError) {
      return this.callCentreNumberError;
    }
    if (this.overviewFieldChanged('callCentreEmail') && this.callCentreEmailError) {
      return this.callCentreEmailError;
    }
    return null;
  }

  private overviewFieldChanged(field: keyof FleetOverview): boolean {
    if (!this.configuration) return false;
    return !this.loadedOverview || this.configuration.overview[field] !== this.loadedOverview[field];
  }

  private authConfigurationChanged(): boolean {
    if (!this.configuration?.specialPriceSetId) return true;
    return this.overviewFieldChanged('fleetAuthType') ||
      this.overviewFieldChanged('fleetAuthSubType') ||
      this.loadedAuthFieldsState !== this.authFieldsEditableState(this.configuration.authFields);
  }

  private authFieldsEditableState(fields: FleetAuthRequirement[]): string {
    return JSON.stringify(fields.map(field => [
      field.fleetAuthFieldRequirementId,
      field.fleetAuthFieldId,
      field.isRequired,
      field.isNew,
      field.isNew ? field.fleetAuthFieldName : null,
      field.isNew ? field.dataType : null,
      field.isNew ? field.tooltipText : null
    ]));
  }

  private configurationForSave(): FleetConfiguration {
    const configuration = this.copy(this.configuration as FleetConfiguration);
    if (
      configuration.specialPriceSetId &&
      (configuration.documentBase64 ?? null) === this.loadedDocumentBase64
    ) {
      delete configuration.documentBase64;
    }
    if (this.availabilityMode === 'franchises') {
      configuration.sellers = [];
    } else if (this.availabilityMode === 'branches') {
      configuration.franchiseIds = [];
    }
    if (this.availabilityMode === 'branches' && this.merchantMode === 'shared') {
      const merchantNumber = this.sharedMerchantNumber?.trim() || null;
      configuration.sellers.forEach(seller => seller.merchantNumber = merchantNumber);
    }
    return configuration;
  }

  private distinctMerchantNumbers(sellers: FleetSeller[]): string[] {
    return [...new Set(
      sellers
        .map(seller => seller.merchantNumber?.trim())
        .filter((value): value is string => !!value)
    )];
  }

  private searchOptionsWithSelected<T, K extends keyof T>(
    existing: T[],
    incoming: T[],
    key: K,
    selectedKeys: Array<T[K]>
  ): T[] {
    const values = new Map(incoming.map(item => [item[key], item]));
    const selected = new Set(selectedKeys);
    existing.filter(item => selected.has(item[key])).forEach(item => values.set(item[key], item));
    return [...values.values()];
  }

  private suggestionsFor(values: Array<string | null | undefined>, query: string): string[] {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    return [...new Set(values.map(value => value?.trim()).filter((value): value is string => !!value))]
      .filter(value => value.toLowerCase().includes(needle))
      .sort((left, right) => this.compareText(left, right))
      .slice(0, 8);
  }

  private setLoadError(error: any, fallback: string): void {
    this.loadError = this.errorMessage(error, fallback);
  }

  private handleLookupError(error: any, fallback: string): void {
    if (error?.status === 503) {
      this.setLoadError(error, fallback);
      return;
    }
    this.notification.handleError(error, fallback);
  }

  private errorMessage(error: any, fallback: string): string {
    if (error?.status === 403) {
      return 'You do not have permission to use Fleet Maintenance.';
    }
    const detail = error?.error?.detail;
    return typeof detail === 'string' ? detail : detail?.message || fallback;
  }

  private pricingMethodValidationMessage(methodId: number | null, value: number | null): string | null {
    if (methodId === null || value === null) return 'Choose a pricing method and value.';
    if (!Number.isFinite(value)) return 'Pricing value must be a valid number.';
    if (value < 0) return 'Pricing values cannot be negative.';
    if (!this.hasAtMostTwoDecimalPlaces(value)) return 'Pricing values may have at most two decimal places.';
    if (methodId === 1 && value >= 100) return 'FGPP must be below 100%.';
    if (methodId === 2 && value > 100) return 'Retail Less may not exceed 100%.';
    return null;
  }

  private fixedStockCodePriceValidationMessage(value: number | null): string | null {
    if (value === null || !Number.isFinite(value) || value <= 0) {
      return 'Fixed stock code prices excluding VAT must be greater than zero.';
    }
    return this.hasAtMostTwoDecimalPlaces(value)
      ? null
      : 'Fixed stock code prices may have at most two decimal places.';
  }

  private rimValidationMessage(rim: number | string | null | undefined): string | null {
    if (rim === null || rim === undefined || (typeof rim === 'string' && !rim.trim())) return null;
    const numericRim = Number(rim);
    if (!Number.isFinite(numericRim)) return 'Enter a valid rim size.';
    if (numericRim <= 0) return 'Rim overrides must be greater than zero.';
    if (numericRim > 999.99) return 'Rim overrides may not exceed 999.5 inches.';
    return Number.isInteger(numericRim * 2)
      ? null
      : 'Use a whole or half-inch rim size, for example 16 or 16.5.';
  }

  private normaliseRim(rim: number | string | null | undefined): number | null {
    if (rim === null || rim === undefined || (typeof rim === 'string' && !rim.trim())) return null;
    return Number(rim);
  }

  private hasAtMostTwoDecimalPlaces(value: number): boolean {
    return Math.abs(value * 100 - Math.round(value * 100)) < 1e-8;
  }

  private stockExceptionValue(item: FleetSkuException): number {
    return item.price ?? item.priceMatrixMethodValue ?? 0;
  }

  private compareText(left: string | null | undefined, right: string | null | undefined): number {
    return (left || '').localeCompare(right || '', undefined, { sensitivity: 'base' });
  }

  private captureSaveError(error: any, fallback: string): void {
    const detail = error?.error?.detail;
    const section = this.isFleetSection(detail?.section) ? detail.section : 'overview';
    const rowId = typeof detail?.rowId === 'number' ? detail.rowId : null;
    this.setSaveError(this.errorMessage(error, fallback), section, rowId);
  }

  private setSaveError(message: string, section: FleetSection, rowId: number | null = null): void {
    this.saveError = { message, section, rowId };
    this.activeTabIndex = ({
      overview: 0,
      pricingRules: 1,
      stockCodeExceptions: 2,
      availability: 3,
      authorisation: 4,
      document: 5
    } as Record<FleetSection, number>)[section];
    this.notification.showWarning('The fleet was not saved. Review the highlighted section and try again.');
  }

  private isFleetSection(value: unknown): value is FleetSection {
    return value === 'overview' || value === 'pricingRules' || value === 'stockCodeExceptions' ||
      value === 'availability' || value === 'authorisation' || value === 'document';
  }

  private canLeaveEditor(): boolean {
    return !this.isDirty || window.confirm('Discard your unsaved fleet changes?');
  }

  private blankNewField(): FleetAuthRequirement {
    return {
      fleetAuthFieldRequirementId: null,
      fleetAuthFieldId: null,
      fleetAuthFieldName: '',
      tooltipText: null,
      dataType: 1,
      displayRank: null,
      modelField: null,
      isRequired: false,
      isNew: true
    };
  }

  private copy<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
  }
}
